/**
 * "Clear" product flow — a chromeless, self-contained variant of the web dashboard
 * served from `/clear-*` paths (`/clear-calendar`, `/clear-login`, `/clear-signup`,
 * `/clear-fyp`).
 *
 * The flow is URL-based: there is no per-user flag. Anything reachable from the
 * clear auth screens must route to the clear counterpart of a path instead of the
 * regular one, otherwise a visitor silently lands back in the full product. Rather
 * than sprinkling `location.pathname.startsWith(...)` checks at each of those call
 * sites, they all funnel through `flowPath()` below.
 */

/** Paths that have a `/clear-` counterpart. Keep in sync with the routes in website.tsx. */
export const MIRRORED_ROOTS = [
  'calendar',
  'login',
  'signup',
  'fyp',
  'account',
  'account-security',
  'account-backup',
  'connections',
  'campaigns',
] as const;

// Longest first so `/account-security` is not matched by the shorter `account`
// alternative. (The trailing lookahead below already prevents that, but the
// ordering keeps the intent obvious if the lookahead ever changes.)
const MIRRORED_PATTERN = new RegExp(
  `^/(${[...MIRRORED_ROOTS].sort((a, b) => b.length - a.length).join('|')})(?=$|[?#/])`,
);

/**
 * The live pathname, or '' where there is no window — these helpers are imported
 * by modules that also run under the node test environment (and would run under
 * SSR), where touching `window` throws before the caller can pass a pathname.
 * No window means no clear flow, which is the correct default.
 */
const currentPathname = (): string =>
  typeof window === 'undefined' ? '' : window.location.pathname;

/** True when the current location belongs to the clear flow. */
export const isClearFlow = (pathname: string = currentPathname()): boolean =>
  pathname.startsWith('/clear-');

/** True for any auth screen (regular or clear) — used to skip JWT auth attempts. */
export const isAuthPath = (pathname: string = currentPathname()): boolean =>
  ['/login', '/signup', '/fyp', '/clear-login', '/clear-signup', '/clear-fyp'].includes(pathname);

/**
 * Translate a regular-product path to its clear counterpart while in the clear flow;
 * a no-op everywhere else. Preserves query strings — `/calendar?welcome=true`
 * becomes `/clear-calendar?welcome=true`.
 */
export const flowPath = (path: string, pathname?: string): string => {
  if (!isClearFlow(pathname)) return path;
  return path.replace(MIRRORED_PATTERN, '/clear-$1');
};
